import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, Observable, ReplaySubject, Subject } from "rxjs";
import { ApiService } from "../constants/api.service";
import { EventsService } from "../constants/events.service";
import { ToastrService } from "ngx-toastr";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { isBefore } from "src/app/utils/membership-utils";
import { ClubItem, Person } from "src/app/pages/registration/membership/membership-club/membership-club.service";
import moment from "moment";
import { Step } from "src/app/pages/registration/membership/membership.component";
import { DigitalSignature, DigitalSignatureContractType } from "../models/digital-signature.model";
import { Select } from "@ngxs/store";
import { UserState } from "src/app/store/user/user.store";
import { User } from "../models/user.model";

export interface OrderItemList {
  id: number;
  additionalFieldFirst?: string;
  additionalFieldSecond?: any;
  price?: number;
  type?: string;
  person?: any;
  name?: string;
  qty?: number;
  membership?: any;
  parentItemId?: number;
  packageNumber?: number;
}

export type TransactionStatus = "APPROVED" | "DECLINED" | "ERROR" | "HELD_FOR_REVIEW" | "PENDING" | "SCHEDULED";

export interface Order {
  id: number;
  orderNo?: string;
  orderItemList?: OrderItemList[];
  clubItem?: ClubItem;
  orderItemMembershipTypeList?: OrderItemList[];
  orderItemNotMembershipTypeList?: OrderItemList[];
  orderItemBulkList?: OrderItemList[];
  settlementDate?: string;
  currencyCode?: string;
  totalPrice?: number;
  operationDate?: string;
  updateDate: string;
  paymentMethod: string;
  expanded?: boolean;
  paymentFormUrl?: string;
  parentItemId?: number;
  transactionStatus: TransactionStatus;
}

export enum MembershipType {
  ATHLETE = "ATHLETE",
  NON_ATHLETE = "NON_ATHLETE",
  EVENT_LICENSE = "EVENT_LICENSE",
  PRACTICE_LICENSE = "PRACTICE_LICENSE",
}

export enum NonAthleteCategory {
  // ATHLETE = "ATHLETE",
  COACH = "COACH",
  // CLUB_ADMIN = "CLUB_ADMIN",
  OFFICIAL = "OFFICIAL",
  // VENDOR = "VENDOR",
  // ATHLETIC_TRAINER = "ATHLETIC_TRAINER",
  VOLUNTEER = "VOLUNTEER",
  OTHER = "OTHER",
  // COLLEGE_COACH = "COLLEGE_COACH",
};

export enum Grade {
  PRE_K = "PRE_K",
  KINDERGARTEN = "KINDERGARTEN",
  GRADE_1 = "GRADE_1",
  GRADE_2 = "GRADE_2",
  GRADE_3 = "GRADE_3",
  GRADE_4 = "GRADE_4",
  GRADE_5 = "GRADE_5",
  GRADE_6 = "GRADE_6",
  GRADE_7 = "GRADE_7",
  GRADE_8 = "GRADE_8",
  GRADE_9 = "GRADE_9",
  GRADE_10 = "GRADE_10",
  GRADE_11 = "GRADE_11",
  GRADE_12 = "GRADE_12",
  GRADUATED = "GRADUATED"
};

export const GRADES = [
  { value: Grade.PRE_K, label: "Pre-K / Preschool" },
  { value: Grade.KINDERGARTEN, label: "Kindergarten" },
  { value: Grade.GRADE_1, label: "1st Grade" },
  { value: Grade.GRADE_2, label: "2nd Grade" },
  { value: Grade.GRADE_3, label: "3rd Grade" },
  { value: Grade.GRADE_4, label: "4th Grade" },
  { value: Grade.GRADE_5, label: "5th Grade" },
  { value: Grade.GRADE_6, label: "6th Grade" },
  { value: Grade.GRADE_7, label: "7th Grade" },
  { value: Grade.GRADE_8, label: "8th Grade" },
  { value: Grade.GRADE_9, label: "9th Grade" },
  { value: Grade.GRADE_10, label: "10th Grade" },
  { value: Grade.GRADE_11, label: "11th Grade" },
  { value: Grade.GRADE_12, label: "12th Grade" },
  { value: Grade.GRADUATED, label: "Graduated" },
];

export const NON_ATHLETE_CATEGORIES = [
  // {value: NonAthleteCategory.ATHLETE, label: "Athlete"},
  { value: NonAthleteCategory.COACH, label: "Coach" },
  // { value: NonAthleteCategory.CLUB_ADMIN, label: "Club admin" },
  { value: NonAthleteCategory.OFFICIAL, label: "Official" },
  // { value: NonAthleteCategory.VENDOR, label: "Vendor" },
  // { value: NonAthleteCategory.ATHLETIC_TRAINER, label: "Athletic trainer" },
  { value: NonAthleteCategory.VOLUNTEER, label: "Volunteer" },
  { value: NonAthleteCategory.OTHER, label: "Other" },
  // { value: NonAthleteCategory.COLLEGE_COACH, label: "College coach" },
];


export const CLUB_CATEGORIES = [
  { value: "", label: "All Clubs" },
  { value: "CLUB_A", label: "Level A" },
  { value: "CLUB_B", label: "Level B" },
  { value: "CLUB_C", label: "Level C" },
  { value: "TIER_1", label: "Tier 1" },
  { value: "TIER_2", label: "Tier 2" },
  { value: "TIER_3", label: "Tier 3" },
];

@Injectable({ providedIn: "root" })
export class MembershipService {
  private membershipTypeSubject = new ReplaySubject<MembershipType>(1);
  membershipType$ = this.membershipTypeSubject.asObservable();
  membershipType: MembershipType | undefined;
  private activeOrderUpdated = new Subject<void>();
  activeOrderUpdated$ = this.activeOrderUpdated.asObservable();
  private _standardAthleteList: any;
  standard2YearsAthleteList: any;
  private _extendedAthleteList: any;
  extended2YearsAthleteList: any;
  private _activeOrderItemList: any;
  private eventId: number = 0;
  private event: any;
  onBackClicked$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  leagueId: number = 0;

  signatureForm: FormGroup = this.fb.group({
    firstName: ["", Validators.required],
    lastName: ["", Validators.required],
  });
  agreed = false;

  contractsSigned$: BehaviorSubject<DigitalSignature[]> = new BehaviorSubject<DigitalSignature[]>([]);

  private _coverageTypes = [
    {
      id: 1,
      title: "Extended benefit",
      titleNote: "Coverage",
      selectedAthletes: 0,
      note1:
        "This membership permits participation in non-AAU events. For team competitions, all members, coaches, and staff need AAU extended coverage. In individual events, each participant must have extended coverage and be supervised by a registered AAU coach.",
      note2: "*Please note that memberships are non-refundable once issued.",
      expanded: false,
      totalPrice: 0,
      name: "EXTENDED",
    },
    {
      id: 2,
      title: "Standard coverage",
      titleNote: "Coverage",
      selectedAthletes: 0,
      note1:
        "A standard coverage membership is required for athletes and team members to participate in licensed AAU events.",
      note2: "*Please note that memberships are non-refundable once issued.",
      expanded: false,
      totalPrice: 0,
      name: "STANDARD",
    },
  ];
  justSynckedId: any;
  allMembers: any;
  onFormPage: boolean = false;
  currentStep$: BehaviorSubject<Step | undefined> = new BehaviorSubject<Step | undefined>(undefined);

  @Select(UserState.authUser)
  public authUser$!: Observable<User>;

  user: User | undefined;
  activeOrder: any;
  constructor(
    private api: ApiService,
    private eventsService: EventsService,
    private toastr: ToastrService,
    private fb: FormBuilder
  ) {
    this.authUser$.pipe().subscribe((user: any) => {
      this.user = user;
    });
  }

  async fetchEvent(eventId: number) {
    try {
      this.event = await firstValueFrom(this.eventsService.getEvent(eventId));
      return this.event;
    } catch (e) {
      this.toastr.error("We could not get the event information");
      console.error(e);
    }
  }

  async initData(eventId: number): Promise<void | "ERROR"> {
    this.eventId = eventId;
    await this.fetchEvent(eventId);
    if (
      this.event &&
      this.event.eventConfig &&
      this.event.eventConfig.membershipType
    ) {
      // if (this.event && this.event.id) {
      if (this.event.eventConfig.membershipType === "AAU_ATHLETE_MEMBERSHIP" || this.event.eventConfig.membershipType === "USAGR_ATHLETE_MEMBERSHIP") {
        // if (this.event.id === 334091) {
        this.membershipType = MembershipType.ATHLETE;
        this.membershipTypeSubject.next(this.membershipType);
      } else if (
        this.event.eventConfig.membershipType === "AAU_NON_ATHLETE_MEMBERSHIP" || this.event.eventConfig.membershipType === "USAGR_NON_ATHLETE_MEMBERSHIP"
        // this.event.id === 334192
      ) {
        this.membershipType = MembershipType.NON_ATHLETE;
        this.membershipTypeSubject.next(this.membershipType);
      } else if (this.event.eventConfig.membershipType === "AAU_EVENT_LICENSING" || this.event.eventConfig.membershipType === "USAGR_EVENT_LICENSING") {
        this.membershipType = MembershipType.EVENT_LICENSE;
        this.membershipTypeSubject.next(this.membershipType);
      } else if (this.event.eventConfig.membershipType === "AAU_PRACTICE_LICENSING" || this.event.eventConfig.membershipType === "USAGR_PRACTICE_LICENSING") {
        this.membershipType = MembershipType.PRACTICE_LICENSE;
        this.membershipTypeSubject.next(this.membershipType);
      }
    } else {
      return "ERROR";
    }
    await this.getAllowedOrderItems(false);

    await this.getOrderItems();

    this.fetchContractsData(this.membershipType, this.eventId, this.activeOrder);

  }


  async fetchContractsData(membershipType: MembershipType | undefined, eventId: number, order: any) {
    if (membershipType === MembershipType.ATHLETE) {
      try {
        const response: DigitalSignature = <DigitalSignature>await firstValueFrom(this.api.checkDigitalSign(
          { digitalSignatureContractId: DigitalSignatureContractType.AAU_ATHLETE_TERMS_AND_CONDITIONS, attributes: eventId + "," + order?.id }));
        if (response) {
          this.agreed = true;
          this.addOrReplaceContract(response);
        }
      } catch (e) {
        this.toastr.error("Error fetching digital signature")
        console.error(e);
      }
    } else if (membershipType === MembershipType.NON_ATHLETE) {
      try {
        const response: DigitalSignature = <DigitalSignature>await firstValueFrom(this.api.checkDigitalSign(
          { digitalSignatureContractId: DigitalSignatureContractType.AAU_NON_ATHLETE_TERMS, attributes: eventId + "," + order.id }));
        if (response) {
          this.agreed = true;
          this.addOrReplaceContract(response);
        }
      } catch (e) {
        this.toastr.error("Error fetching digital signature")
        console.error(e);
      }
    } else if (membershipType === MembershipType.EVENT_LICENSE) {

    } else if (membershipType === MembershipType.PRACTICE_LICENSE) {

    }
  }

  addOrReplaceContract(signature: DigitalSignature) {
    const existingArray = this.contractsSigned$.getValue();
    const alreadyExistContractIndex = existingArray.findIndex(x => x.digitalSignatureContractId === signature.digitalSignatureContractId);
    if (alreadyExistContractIndex >= 0) {
      existingArray[alreadyExistContractIndex] = signature;
    } else {
      existingArray.push(signature);
    }
    this.contractsSigned$.next(existingArray);
  }


  validContract(firstName: string, lastName: string, hideToastr?: boolean): boolean {
    if (!this.user) {
      if (!hideToastr) {
        this.toastr.error("No user found");
      }
      return false;
    }
    if (this.areFormSimilarToUser(firstName, lastName, this.user)) {
      return true;
    } else {
      if (!hideToastr) {
        this.toastr.error(`Names are not similar to the current user's (${this.user.firstName}  ${this.user.lastName})`);
      }
      return false;
    }
  }

  areFormSimilarToUser(firstName: string, lastName: string, user: any) {
    const formFirst = firstName.trim().toLowerCase();
    const formLast = lastName.trim().toLowerCase();
    const userFirst = user.firstName.trim().toLowerCase();
    const userLast = user.lastName.trim().toLowerCase();

    const firstNameMatch = userFirst === formFirst;
    const lastNameMatch = userLast === formLast;


    if (firstNameMatch && lastNameMatch) {
      return true;
    } else {
      return false;
    }
  }

  public async getAllowedOrderItems(invalidateCache: boolean) {
    try {
      const data: any = await firstValueFrom(
        this.api.getAllowedOrderItems(this.eventId, invalidateCache)
      );
      await this.updateOrderStatus(data);
    } catch (e) {
      this.toastr.error("We could not get the allowed order items");
      console.error(e);
    }
  }

  isMembershipActive(person: any): boolean {
    const memberships = this.getActiveMemberships(person);
    if (!memberships.length) {
      return false;
    }
    return memberships.some((m: any) => {
      if (!m.endDate) {
        return false;
      }
      if (m.status === "APPROVED") {
        return moment(m.endDate, "YYYY-MM-DD").isSameOrAfter(
          moment(this.event.eventEndDate, "YYYY-MM-DD")
        );
      }
      return false;
    });
  }

  isMembershipPendingStatus(person: any): boolean {
    const memberships = this.getActiveMemberships(person);
    if (!memberships.length) {
      return false;
    }
    return memberships.some((m: any) => {
      if (!m.endDate) {
        return false;
      }
      if (m.status === "PENDING") {
        return moment(m.endDate, "YYYY-MM-DD").isSameOrAfter(
          moment(this.event.eventEndDate, "YYYY-MM-DD")
        );
      }
      return false;
    });
  }

  isAthleteOrNonAthleteRegistered(person: any): boolean {
    const memberships = person.memberships;
    if (!memberships?.length) {
      return false;
    }
    return memberships.some((m: any) => {
      if (!m.endDate) {
        return false;
      }
      if (m.status) {
        return moment(m.endDate, "YYYY-MM-DD").isSameOrAfter(
          moment(this.event.eventEndDate, "YYYY-MM-DD")
        );
      }
      return false;
    });
  }

  async ableToGoToStep2(): Promise<boolean> {
    if (this.eventId) {
      const data: any = await firstValueFrom(
        this.api.getAllowedOrderItems(this.eventId, false)
      );
      return data && data.length;
    } else {
      return false;
    }
  }

  async extendedCoverageAllowed(membershipId: number): Promise<boolean> {
    if (this.eventId) {
      const data: any = await firstValueFrom(
        this.api.extendedCoverageAllowed(this.eventId, membershipId)
      );
      return data;
    } else {
      return false;
    }
  }

  extendedBulkCoverage(membershipIds: number[]): Observable<any> {
    return this.api.extendedBulkCoverage(this.eventId, membershipIds);
  }

  async extendedCoverage(membershipId: number): Promise<boolean> {
    if (this.eventId) {
      const data: any = await firstValueFrom(
        this.api.extendedCoverage(this.eventId, membershipId)
      );
      return data;
    } else {
      return false;
    }
  }

  getActiveMemberships(person: any): any[] {
    if (!person.memberships?.length || !this.membershipType) {
      return [];
    }

    return person.memberships.filter((m: any) => {
      const type = this.membershipType;
      return (
        (type === MembershipType.NON_ATHLETE && m.isNonAthlete) ||
        (type === MembershipType.ATHLETE && !m.isNonAthlete)
      );
    });
  }

  private async getOrderItems() {
    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(this.api.getActiveOrder(this.eventId)), false
      );
    } catch (e) {
      this.toastr.error("We could not get the current order items");
      console.error(e);
    }
  }

  async fetchAllPersons(minAge: number, eventId?: any) {
    try {
      let evId = eventId ? eventId : this.eventId;
      let data = await firstValueFrom(
        this.api.getPersons("", 0, 3000, minAge, evId, null)
      );
      this.allMembers = data;
      return this.allMembers;
    } catch (e) {
      console.error(e);
      this.toastr.error("We could not get the persons data");
    }
  }

  async findMemberships(person: any, eventId: any) {
    if (!this.allMembers) {
      await this.fetchAllPersons(-1, eventId);
    }
    if (!this.allMembers) {
      return [];
    }

    const p = this.allMembers.items?.find((x: any) => x.id === person.id);
    if (!p) {
      return [];
    }

    if (p.memberships?.length) {
      return p.memberships.filter((m: any) => {
        const type = this.membershipType;
        return (
          (type === MembershipType.NON_ATHLETE && m.isNonAthlete) ||
          (type === MembershipType.ATHLETE && !m.isNonAthlete)
        );
      });
    }
    return [];
  }

  validRequiredFieldWithoutValidatingVerified(person: any, membershipType: MembershipType): boolean {
    if (
      !person.primarySport ||
      !person.address ||
      !person.birthDate ||
      !person.country ||
      !person.email ||
      !person.firstName ||
      !person.lastName ||
      !person.gender ||
      !person.zipCode ||
      !person.city ||
      (person.country && (person.country.id === 228 || person.country.id === 38) && !person.state) ||
      !person.phoneNumber
    ) {
      return false;
    }
    if (membershipType === MembershipType.NON_ATHLETE) {
      if (!person.defaultMembershipProgram) {
        return false;
      }

      if (!person.defaultNonAthleteCategory || person.defaultNonAthleteCategory.length === 0) {
        return false;
      }
    } else if (membershipType === MembershipType.ATHLETE) {
      const isGradeRequired = person.age >= 0 && person.age <= 21 && person.primarySport &&
        [11, 5, 6, 80, 7, 16, 50, 8, 13, 17, 21, 35, 33, 34, 31, 29, 28, 37, 27, 32, 38, 42, 45, 43, 44, 49, 52, 61, 60, 41, 78, 15, 82].includes(person.primarySport.id);
      
      if (isGradeRequired && !person.defaultGrade) {
        return false;
      }
    }
    return true;
  }

  validRequiredFields(person: any, membershipType: MembershipType): boolean {
    if (
      !person.primarySport ||
      !person.address ||
      !person.birthDate ||
      !person.country ||
      !person.email ||
      !person.firstName ||
      !person.lastName ||
      !person.gender ||
      !person.zipCode ||
      !person.city ||
      (person.country && (person.country.id === 228 || person.country.id === 38) && !person.state) ||
      !person.phoneNumber
    ) {
      return false;
    }
    if (membershipType === MembershipType.NON_ATHLETE) {
      if (!person.defaultMembershipProgram) {
        return false;
      }

      if (!person.defaultNonAthleteCategory || person.defaultNonAthleteCategory.length === 0) {
        return false;
      }

      if (!person.isVerificationNotMandatory) {
        if (person.verificationStatus !== 'APPROVED') {
          return false;
        }

        if (this.hasVerificationMismatch(person)) {
          return false;
        }
      }

    } else if (membershipType === MembershipType.ATHLETE) {
      const isGradeRequired = person.age >= 0 && person.age <= 21 && person.primarySport &&
        [11, 5, 6, 80, 7, 16, 50, 8, 13, 17, 21, 35, 33, 34, 31, 29, 28, 37, 27, 32, 38, 42, 45, 43, 44, 49, 52, 61, 60, 41, 78, 15, 82].includes(person.primarySport.id);
      
      if (isGradeRequired && !person.defaultGrade) {
        return false;
      }
      //   if (!person.photoUrl) {
      //     return false;
      //   }
    }
    return true;
  }

  getVerificationMismatchedFields(person: any): string[] {
    const mismatchedFields: string[] = [];
    if (!person || person.verificationStatus !== 'APPROVED' || !person.verificationData) return mismatchedFields;

    const profileData = {
      firstName: person.firstName,
      lastName: person.lastName,
      dateOfBirth: person.birthDate
    };

    const verificationData = person.verificationData;

    const cleanStr = (str: string) => (str || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const profileFullName = cleanStr([person.firstName, person.middleName].filter(Boolean).join(' '));
    const profileFirstName = cleanStr(profileData.firstName);
    const verificationFirstName = cleanStr(verificationData.firstName);

    if (profileFirstName !== verificationFirstName && profileFullName !== verificationFirstName) {
      mismatchedFields.push('firstName');
    }
    if (profileData.lastName?.toLowerCase() !== verificationData.lastName?.toLowerCase()) mismatchedFields.push('lastName');

    const pDate = profileData.dateOfBirth ? new Date(profileData.dateOfBirth).toISOString().split('T')[0] : null;
    const vDate = verificationData.dateOfBirth ? new Date(verificationData.dateOfBirth).toISOString().split('T')[0] : null;
    if (pDate !== vDate) mismatchedFields.push('dateOfBirth');

    return mismatchedFields;
  }

  hasVerificationMismatch(person: any): boolean {
    return this.getVerificationMismatchedFields(person).length > 0;
  }

  async removePersonFromActiveOrder(ath: any) {
    let orderItem = this._activeOrderItemList.find(
      (x: any) => x.person.id === ath.personId
    );
    if (!orderItem) {
      this.toastr.error("No item found to remove");
      console.error("No item found to remove");
      return;
    }

    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(
          this.api.deleteOrderItem(
            this.eventId,
            orderItem.id,
            orderItem.person.id
          )
        ), true
      );
    } catch (e) {
      this.toastr.error("We could not remove the item from the order");
      console.error(e);
    }
  }

  async removeLicenseFromActiveOrder(lic: any, orderItemList: OrderItemList[]) {
    let allRelatedOrderItems = orderItemList.filter(
      (x: any) => x.packageNumber === lic.item.packageNumber
    );

    if ((allRelatedOrderItems?.length ?? 0) === 0) {
      this.toastr.error("No item found to remove, please reload the page");
      console.error("No item found to remove");
      return;
    }

    const orderItemsToDelete = allRelatedOrderItems.map((x: any) => ({
      id: x.id,
      personId: x.person?.id,
      person: x.person,
      packageNumber: x.packageNumber,
      type: x.type,
    }));

    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(
          this.api.deleteBulkUploadOrder(this.eventId, orderItemsToDelete)
        ), true);
    } catch (e) {
      this.toastr.error("We could not remove the item from the order");
      console.error(e);
    }
  }

  async removeBulkFromShoppingCart(bulk: any, orderItemList: any) {
    let orderItems = bulk.items.map((ath: any) => {
      let orderItem = orderItemList.find(
        (x: any) => x.person.id === ath.person.id
      );
      if (!orderItem) {
        this.toastr.error("No item found to remove for " + ath.personId);
        console.error("No item found to remove for " + ath.personId);
        return;
      }
      return orderItem;
    });
    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(
          this.api.deleteBulkUploadOrder(this.eventId, orderItems)
        ), true
      );
    } catch (e) {
      this.toastr.error("We could not remove the item from the order");
      console.error(e);
    }
  }

  async removeBulkFromActiveOrder(bulk: any) {
    let orderItems = bulk.items.map((ath: any) => {
      let orderItem = this._activeOrderItemList.find(
        (x: any) => x.person.id === ath.personId
      );
      if (!orderItem) {
        this.toastr.error("No item found to remove for " + ath.personId);
        console.error("No item found to remove for " + ath.personId);
        return;
      }
      return orderItem;
    });
    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(
          this.api.deleteBulkUploadOrder(this.eventId, orderItems)
        ), true
      );
    } catch (e) {
      this.toastr.error("We could not remove the item from the order");
      console.error(e);
    }
  }

  async insertBulkOrderItem(bulk: any, coverage?: string) {
    const transformedList = bulk.items.map((item: any) => ({
      itemType: item.itemType,
      personId: item.personId,
      coverage: coverage ?? item.coverage,
      itemReferenceId: item.itemReferenceId,
    }));

    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(
          this.api.addBulkUploadOrders(this.eventId, transformedList)
        ), true
      );
    } catch (e) {
      this.toastr.error("We could not insert the item to the order");
      console.error(e);
    }
  }

  async insertOrderItem(allowedItem: {
    itemType: string;
    personId: number;
    coverage: string;
    membershipTerm?: "TWO_YEARS";
  }) {
    try {
      await this.setActiveOrderItemList(
        await firstValueFrom(
          this.api.insertRosterMembershipOrderItem(this.eventId, allowedItem)
        ), true
      );
    } catch (e) {
      this.toastr.error("We could not insert the item to the order");
      console.error(e);
    }
  }

  async updateOrderStatus(data: any) {
    const _standardAthleteList: any[] = [];
    const standard2YearsAthleteList: any[] = [];
    const _extendedAthleteList: any[] = [];
    const extended2YearsAthleteList: any[] = [];
    for (const m of this._coverageTypes) {
      m.selectedAthletes = m.totalPrice = 0;
    }

    for (const element of data) {
      // const memberships = await this.findMemberships(element.person, this.eventId);

      // const allMembershipsAreUpToDate = memberships.some((m: any) => {
      //   if (!m.endDate) {
      //     return false;
      //   }
      //   return moment(m.endDate, "YYYY-MM-DD").isSameOrAfter(
      //     moment(this.event.eventEndDate, "YYYY-MM-DD")
      //   );
      // });

      // if (allMembershipsAreUpToDate) {
      //   continue;
      // }

      // if (element.paid) {
      //   console.log(element);
      //   continue;
      // }
      const isExtended = element.coverage === "EXTENDED";
      const isTwoYears = element.membershipTerm && element.membershipTerm === "TWO_YEARS";

      const list = (isExtended && !isTwoYears) ? _extendedAthleteList : (!isExtended && !isTwoYears) ? _standardAthleteList : (isExtended && isTwoYears) ? extended2YearsAthleteList : standard2YearsAthleteList;
      const coverageType = this._coverageTypes[isExtended ? 0 : 1];

      list.push(element);

      if (element.selected) {
        coverageType.selectedAthletes++;
        coverageType.totalPrice += element.price;
      }
    }

    this._standardAthleteList = _standardAthleteList;
    this._extendedAthleteList = _extendedAthleteList;

    this.standard2YearsAthleteList = standard2YearsAthleteList;
    this.extended2YearsAthleteList = extended2YearsAthleteList;


    this.activeOrderUpdated.next();
  }

  getMembershipType(): MembershipType {
    if (!this.membershipType) {
      throw new Error("Membership type has not been initialized");
    }
    return this.membershipType;
  }

  isNonAthlete(): boolean {
    return this.membershipType === MembershipType.NON_ATHLETE;
  }

  async setActiveOrderItemList(data: any, invalidateCache: boolean) {
    this.activeOrder = data;
    if (data && data.orderItemList) {
      this._activeOrderItemList = data.orderItemList;
      await this.getAllowedOrderItems(invalidateCache);
    } else {
      this._activeOrderItemList = [];
      this.toastr.error("No items in current active order");
      console.error(data);
    }
  }

  orderHasItems(): boolean {
    if (this._activeOrderItemList?.length > 0) {
      return true;
    } else {
      return false
    }
  }

  isShoppingStepCartAllowed(): boolean {
    if (this.orderHasItems() && this.agreed) {
      return true;
    }
    return false;
  }

  getCoverageTypes() {
    return this._coverageTypes;
  }

  getStandardCoveragePersonList() {
    return this._standardAthleteList;
  }

  getExtendedCoveragePersonList() {
    return this._extendedAthleteList;
  }

  getActiveOrderList() {
    return this._activeOrderItemList;
  }

  getPersonPhotoUrl(athlete: any) {
    if (!athlete || !athlete.photoUrl || athlete.photoUrl === "") {
      return null;
    }

    if (athlete.imageVersion === undefined || athlete.imageVersion === null) {
      return athlete.photoUrl;
    }

    return `${athlete.photoUrl}?v=${athlete.imageVersion}`;
  }

  updateMembershipOrdersArrayItems(orders: Order[]): Order[] {
    return orders.map((order) => this.updateMembershipOrderItems(order));
  }

  updateMembershipOrderItems(order: Order): Order {
    const items = order?.orderItemList;

    if (!Array.isArray(items) || items.length === 0) {
      return order;
    }

    order.orderItemMembershipTypeList = [];
    order.orderItemBulkList = [];
    order.orderItemNotMembershipTypeList = [];

    for (const item of items) {
      const isMembership =
        (item?.type === "MEMBERSHIPFEE" &&
          (item.additionalFieldFirst === "EXTENDED" ||
            item.additionalFieldFirst === "STANDARD"))
        || item?.type === "BACKGROUND_CHECK_FEE" || item?.type === "EVENT_LICENSE_FEE" || item?.type === "PRACTICE_LICENSE_FEE" || item?.type === "MPE_FEE" || item?.type === "EVENT_LICENSE_CHANGE_FEE" || item?.type === "MEMBERSHIP_EXTEND_COVERAGE_DIFF_FEE";

      if (isMembership && (!item.parentItemId)) {
        order.orderItemMembershipTypeList.push(item);
      } else if (isMembership && item.parentItemId) {
        order.orderItemBulkList.push(item);

      } else {
        const sameNameItem = order.orderItemNotMembershipTypeList.find(
          (x) => x.name === item.name
        );
        if (
          sameNameItem &&
          sameNameItem.qty &&
          sameNameItem.price &&
          item.qty &&
          item.price
        ) {
          sameNameItem.qty += item.qty;
          sameNameItem.price += item.price;
        } else {
          order.orderItemNotMembershipTypeList.push(item);
        }
      }
    }
    return order;
  }

  public isUSAGRMembership(event: any) {
    if (event && event.ratingSystems && (event.ratingSystems.length > 0)) {
      const ratingSystem = event.ratingSystems[0];

      return (ratingSystem.id === 29974);
    }

    return false;
  }

  public isAAUMembership(event: any) {
    if (event && event.ratingSystems && (event.ratingSystems.length > 0)) {
      const ratingSystem = event.ratingSystems[0];

      return (ratingSystem.id === 40);
    }

    return false;
  }

  public isJKFAthleteMembership(event: any) {
    if (event && event.ratingSystems && (event.ratingSystems.length > 0)) {
      const ratingSystem = event.ratingSystems[0];

      return (ratingSystem.id === 29988);
    }

    return false;
  }

  public isMembershipEvent(event: any) {
    return (this.isUSAGRMembership(event) || this.isAAUMembership(event) || this.isJKFAthleteMembership(event));
  }
}
